import { Flavor } from '../flavor';
import { UserDefaultFolderPrefix } from '../teams';

type NonNullable<T> = Exclude<T, null | undefined>;

/**
 * Checks if the given `value` is an email
 * This is using same regex as Angular.
 * See: https://github.com/angular/angular/blob/15.2.2/packages/forms/src/validators.ts#L126
 */
export function isEmail(email: string): boolean {
  return /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(
    email,
  );
}

export function isEnumValue<T>(value: unknown, enumType: T): value is T[keyof T] {
  const values = Object.values(enumType as any);
  return values.includes(value);
}

/** Checks if value is an array of emails */
export function isEmailArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => isEmail(v));
}

/** Get a random number between `min` and `max` with decimals */
export function randomNumber(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}

/** Checks if value is `null` or `undefined` */
export function isNone<T = unknown>(value: T | null | undefined): value is null | undefined {
  return value === null || typeof value === 'undefined';
}

export function isNumber(value: unknown): value is number {
  return typeof value === 'number' && !isNaN(value);
}

export function isObject(value: unknown): value is Object {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Checks if value is NOT `null` or `undefined` */
export function isSome<T = unknown>(value: T | null | undefined): value is NonNullable<T> {
  return !isNone(value);
}

/** Checks if value is non empty string */
export function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

/** Checks if value is a array of non empty strings */
export function isNonEmptyStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => isNonEmptyString(v));
}

/** Checks if value is a array of strings */
export function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

/** Checks if value is a boolean */
export function isBooleanValue(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

/** Checks is it's a valid domain */
export function isValidDomain(value: string): boolean {
  return /^(?!.* .*)(?:[a-z0-9][a-z0-9-]{0,61}[a-z0-9]\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/.test(
    value,
  );
}

export function getUserDefaultAlbumName(
  flavor: Flavor,
  prefix: UserDefaultFolderPrefix,
  email: string,
): string {
  if (prefix === 'legacy') {
    return `User ${email}`;
  } else {
    return `${flavor === 'uc' ? 'UploadCam' : 'SchoolCam'} ${email}`;
  }
}

/** Converts a path relative to the team's storage folder into a full Firebase Storage path. */
export function getTeamFirebaseStoragePath(teamId: string, path: string): string {
  if (path.startsWith('/')) {
    path = path.slice(1);
  }

  return `teams/${teamId}/${path}`;
}

export function mapToEnum<T extends Record<string, string>>(
  value: unknown,
  enumObject: T,
  defaultValue: T[keyof T],
): T[keyof T] {
  if (isNonEmptyString(value) && Object.values(enumObject).includes(value as T[keyof T])) {
    return value as T[keyof T];
  }

  return defaultValue;
}

/** Change email to lower case & trim */
export function normalizeEmail(email: string): string {
  return email.toLowerCase().trim();
}

export function normalizeToArray<T>(value: T | T[]): T[] {
  return Array.isArray(value) ? value : [value];
}

export function* chunkArray<T>(arr: readonly T[], chunkSize: number): Generator<T[], void> {
  for (let i = 0; i < arr.length; i += chunkSize) {
    yield arr.slice(i, i + chunkSize);
  }
}

export function withRetries<T>(
  fn: () => Promise<T>,
  /** Number of attempts to try, including the first one */
  attempts: number,
  delayMs: number,
  maxDelayMs: number = 5000,
): Promise<T> {
  return fn().catch((err) => {
    if (attempts > 1) {
      const nextDelay = Math.min(delayMs * 2, maxDelayMs);

      return new Promise((resolve) =>
        setTimeout(() => resolve(withRetries(fn, attempts - 1, nextDelay, maxDelayMs)), delayMs),
      );
    }

    return Promise.reject(err);
  });
}
